import { zodResolver } from '@hookform/resolvers/zod';
import { KbaQuestionType } from '@seapoint/server/enums';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { FormSelectField } from '../../../../design-system/form-select-field/form-select-field';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { type CreateVirtualCardRequest } from '../../hooks/use-create-card';
import { AccountSelectedItem } from './account-selected-item';
import { SecurityQuestionField } from './security-question-field';

const KBA_ANSWER_MAX_LENGTH = 45;

const createCardFormSchema = z.object({
  displayName: z.string().min(1, 'Choose a nickname').max(255, 'Card nickname must be 255 characters or less'),
  accountId: z.string().min(1, 'Please select an account'),
  knowledgeBase: z
    .array(
      z.object({
        type: z.nativeEnum(KbaQuestionType, {
          errorMap: () => ({ message: 'Choose a security question' }),
        }),
        answer: z
          .string()
          .min(1, 'Enter a valid answer')
          .max(KBA_ANSWER_MAX_LENGTH, `Answer must be ${KBA_ANSWER_MAX_LENGTH} characters or less`),
      }),
    )
    .min(1, 'At least one security question is required'),
});

type CreateCardFormValues = z.infer<typeof createCardFormSchema>;

type CreateCardFormProps = {
  accounts: AccountView[];
  onSubmit: (data: CreateVirtualCardRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  children?: ReactNode;
};

export const CreateCardForm = ({ accounts, onSubmit, onCancel, isSubmitting, children }: CreateCardFormProps) => {
  const form = useForm<CreateCardFormValues>({
    resolver: zodResolver(createCardFormSchema),
    defaultValues: {
      displayName: '',
      accountId: accounts.length === 1 ? accounts[0].id : '',
      knowledgeBase: [{ type: '' as KbaQuestionType, answer: '' }],
    },
  });

  const handleSubmit = (data: CreateCardFormValues) => {
    const request: CreateVirtualCardRequest = {
      accountId: data.accountId,
      displayName: data.displayName,
      knowledgeBase: data.knowledgeBase.filter((kb) => kb.type && kb.answer),
    };
    onSubmit(request);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="h-full">
        <Stack space={0} align="stretch" className="h-full">
          <div className="flex-1 overflow-y-auto">
            <Stack space={6} align="stretch" className="px-8 py-6">
              {children && (
                <Inline justify="center" className="pb-2">
                  {children}
                </Inline>
              )}

              <FormField
                control={form.control}
                name="displayName"
                render={({ field, fieldState }) => (
                  <Stack space={1} align="stretch">
                    <FormItem
                      className={cn(
                        'rounded-lg border border-border bg-card px-4 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:outline-hidden',
                        { 'border-destructive': !!fieldState.error },
                      )}
                    >
                      <Stack space={1} align="stretch">
                        <FormLabel className="text-xs font-medium text-muted-foreground">Card nickname</FormLabel>
                        <FormControl>
                          <input
                            type="text"
                            placeholder="e.g. Office Expenses"
                            className="w-full text-base font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden"
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                      </Stack>
                    </FormItem>
                    <FormMessage className="w-full text-left text-destructive" />
                  </Stack>
                )}
              />

              <FormSelectField<CreateCardFormValues>
                label="Account"
                inputName="accountId"
                placeholder="Select account..."
                isTriggerFullWidth
              >
                {accounts.map((account) => (
                  <Select.Item key={account.id} value={account.id}>
                    <AccountSelectedItem account={account} />
                  </Select.Item>
                ))}
              </FormSelectField>

              <SecurityQuestionField<CreateCardFormValues>
                questionFieldName="knowledgeBase.0.type"
                answerFieldName="knowledgeBase.0.answer"
                index={0}
              />
            </Stack>
          </div>

          <div className="border-t border-border">
            <div className="px-8 py-5">
              <Inline space={3} justify="end">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                  Cancel
                </Button>

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create virtual card'}
                </Button>
              </Inline>
            </div>
          </div>
        </Stack>
      </form>
    </FormProvider>
  );
};
